import React from 'react';
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { formatCurrency, formatDate } from '../utils';

/**
 * Componente de tabela para listar empru00e9stimos e suas parcelas
 */
const TabelaEmprestimos = ({ 
  emprestimosFiltrados,
  handlePagamentoClick,
  handleOpenHistorico
}) => {
  return (
    <>
      {emprestimosFiltrados.length === 0 ? (
        <Typography align="center" color="textSecondary" sx={{ my: 4 }}>
          Nenhum empréstimo encontrado com os filtros aplicados.
        </Typography>
      ) : (
        emprestimosFiltrados.map((emprestimo) => (
          <Accordion key={emprestimo.id} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  width: '100%',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: 1
                }}
              >
                <Typography sx={{ width: { xs: '100%', sm: '30%' }, flexShrink: 0, mr: { sm: 3, xs: 0 } }}>
                  <strong>Cliente:</strong> {emprestimo.cliente?.nome}
                </Typography>
                <Typography sx={{ width: { xs: '100%', sm: '20%' }, mr: { sm: 3, xs: 0 } }}>
                  <strong>Valor:</strong> {formatCurrency(emprestimo.valor_total)}
                </Typography>
                <Typography sx={{ width: { xs: '100%', sm: '20%' } }}>
                  <strong>Data:</strong> {emprestimo.data_criacao ? formatDate(emprestimo.data_criacao) : (emprestimo.data_emprestimo ? formatDate(emprestimo.data_emprestimo) : '-')}
                </Typography>
                <Box sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: 1, sm: 0 } }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<PaymentIcon />}
                    onClick={() => handlePagamentoClick(emprestimo)}
                    fullWidth={true}
                  >
                    REALIZAR PAGAMENTO
                  </Button>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {/* MOBILE: Cards verticais para cada parcela */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {[...emprestimo.parcelas]
                  .sort((a, b) => {
                    const dateA = new Date(a.data_vencimento.split('/').reverse().join('-'));
                    const dateB = new Date(b.data_vencimento.split('/').reverse().join('-'));
                    return dateA - dateB;
                  })
                  .map((parcela) => {
                    const isAtrasada = parcela.status === 'atrasado';
                    const isPaga = parcela.status === 'pago';
                    const isPagaParcial = parcela.status === 'pago_parcial';
                    let statusColor = undefined;
                    if (isAtrasada) statusColor = '#d32f2f';
                    else if (isPaga) statusColor = '#388e3c';
                    else if (isPagaParcial) statusColor = '#f57c00';
                    return (
                      <Box key={parcela.id} sx={{ mb: 2, p: 2, bgcolor: isAtrasada ? '#ffebee' : isPaga ? '#e8f5e9' : isPagaParcial ? '#fff3e0' : '#fff', borderRadius: 2, boxShadow: 1 }}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ color: statusColor, mr: 1 }}
                          >
                            Parcela {parcela.numero_parcela}
                          </Typography>
                          <Box flex={1} />
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(parcela.data_vencimento)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2"><b>Vencimento:</b> {formatDate(parcela.data_vencimento)}</Typography>
                          <Typography variant="body2"><b>Valor:</b> {formatCurrency(parcela.valor)}</Typography>
                          <Typography variant="body2"><b>Status:</b> <span style={{ color: statusColor }}>{parcela.status.toUpperCase()}</span></Typography>
                          <Typography variant="body2"><b>Data Pagamento:</b> {formatDate(parcela.data_pagamento)}</Typography>
                          <Button
                            color="primary"
                            variant="outlined"
                            onClick={() => handleOpenHistorico(parcela.id)}
                            size="small"
                            startIcon={<HistoryIcon />}
                            sx={{ mt: 1, width: '100%' }}
                            title="Ver histórico de pagamentos"
                          >
                            Histórico
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
              {/* DESKTOP: Tabela tradicional */}
              <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Parcela</TableCell>
                      <TableCell>Vencimento</TableCell>
                      <TableCell>Valor</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Data Pagamento</TableCell>
                      <TableCell>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...emprestimo.parcelas]
                      .sort((a, b) => {
                        const dateA = new Date(a.data_vencimento.split('/').reverse().join('-'));
                        const dateB = new Date(b.data_vencimento.split('/').reverse().join('-'));
                        return dateA - dateB;
                      })
                      .map((parcela) => {
                        const isAtrasada = parcela.status === 'atrasado';
                        const isPaga = parcela.status === 'pago';
                        const isPagaParcial = parcela.status === 'pago_parcial';
                        return (
                          <TableRow 
                            key={parcela.id}
                            sx={{
                              backgroundColor: isAtrasada ? '#ffebee' : 
                                            isPaga ? '#e8f5e9' : 
                                            isPagaParcial ? '#fff3e0' : 'inherit'
                            }}
                          >
                            <TableCell>{parcela.numero_parcela}</TableCell>
                            <TableCell>{formatDate(parcela.data_vencimento)}</TableCell>
                            <TableCell>{formatCurrency(parcela.valor)}</TableCell>
                            <TableCell>
                              <Typography 
                                color={isAtrasada ? 'error' : 
                                      isPaga ? 'success' : 
                                      isPagaParcial ? 'warning' : 'textPrimary'}
                              >
                                {parcela.status.toUpperCase()}
                              </Typography>
                            </TableCell>
                            <TableCell>{formatDate(parcela.data_pagamento)}</TableCell>
                            <TableCell>
                              <Button
                                color="primary"
                                variant="outlined"
                                onClick={() => handleOpenHistorico(parcela.id)}
                                size="small"
                                startIcon={<HistoryIcon />}
                                title="Ver histórico de pagamentos"
                              >
                                Histórico
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </>
  );
};

export default TabelaEmprestimos;
